import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { RestBidModel, RestTypedBidAttachment } from '@shared/interfaces/bid.interface';
import { RestBulkScopeAndBidModel } from '@shared/interfaces/rest-bulk-scope-and-bid-model';
import { UpdateTypedAttachmentToEntityModel } from '@shared/interfaces/turnover.interface';

@Injectable({
  providedIn: 'root',
})
export class BidsService {
  constructor(private restApiService: RestapiService) {}

  create(bidList: RestBidModel[]): Observable<RestBidModel[]> {
    return this.restApiService.create<RestBidModel[]>(`Bids`, bidList);
  }

  update(bid: RestBidModel): Observable<RestBidModel> {
    return this.restApiService.update<RestBidModel>(`Bids/${bid.id}`, bid);
  }

  reject(id: number): Observable<RestBidModel> {
    return this.restApiService.create<RestBidModel>(`Bids/reject/${id}`, {});
  }

  getBid(bidId: number): Observable<RestBidModel> {
    return this.restApiService.getData<RestBidModel>(`Bids/${bidId}`);
  }

  setAttachment(attachmentToEntity: UpdateTypedAttachmentToEntityModel): Observable<RestTypedBidAttachment> {
    return this.restApiService.create<RestTypedBidAttachment>(
      `Bids/${attachmentToEntity.bidId}/Attachments`,
      attachmentToEntity
    );
  }

  getTurnoverBids(turnoverId: number): Observable<RestBidModel[]> {
    return this.restApiService.getData<RestBidModel[]>(`Bids/turnover/${turnoverId}`);
  }

  getCompanyBids(companyId: number): Observable<RestBidModel[]> {
    return this.restApiService.getData<RestBidModel[]>(`Bids/company/${companyId}`);
  }

  getAllBids(): Observable<RestBidModel[]> {
    return this.restApiService.getData<RestBidModel[]>(`Bids`);
  }

  deleteBidAttachment(bidId: number, attachmentId: number): Observable<boolean> {
    return this.restApiService.delete(`Bids/${bidId}/Attachments/${attachmentId}`);
  }

  getBidAttachments(bidId: number): Observable<RestTypedBidAttachment[]> {
    return this.restApiService.getData<RestTypedBidAttachment[]>(`Bids/${bidId}/Attachments`);
  }

  createBulkScope(bulkScope: RestBulkScopeAndBidModel): Observable<RestBulkScopeAndBidModel> {
    return this.restApiService.create<RestBulkScopeAndBidModel>(`Bids/bulk-scope-and-bid`, bulkScope);
  }

  getBulkScopeList(propertyId: number): Observable<RestBulkScopeAndBidModel[]> {
    return this.restApiService.getData<RestBulkScopeAndBidModel[]>(`Bids/bulk-scope-and-bid/property/${propertyId}`);
  }

  getBulkScope(bulkScopeId: number): Observable<RestBulkScopeAndBidModel> {
    return this.restApiService.getData<RestBulkScopeAndBidModel>(`Bids/bulk-scope-and-bid/${bulkScopeId}`);
  }

  updateBulkScope(bulkScope: RestBulkScopeAndBidModel): Observable<RestBulkScopeAndBidModel> {
    return this.restApiService.update<RestBulkScopeAndBidModel>(`Bids/bulk-scope-and-bid`, bulkScope);
  }

  addUnitsToBulk(bulkScope: RestBulkScopeAndBidModel): Observable<RestBulkScopeAndBidModel> {
    return this.restApiService.update<RestBulkScopeAndBidModel>(`Bids/bulk-scope-and-bid/add-or-update`, bulkScope);
  }

  addBulkTurnoversAttachment(body: UpdateTypedAttachmentToEntityModel): Observable<boolean> {
    return this.restApiService.create<boolean>('Turnovers/bulk/Attachments', body);
  }
}
